import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import ReactECharts from 'echarts-for-react'
import type { EChartsOption } from 'echarts'
import { Loader2 } from 'lucide-react'
import api from '@/lib/api/axios'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

interface CrossObjectEntry {
    date: string
    objectID: string
    objectName?: string
    type?: string
    description?: string
    score: number
    vaultName?: string
}

interface CrossObjectData {
    heatmap: { objects: string[]; dates: string[]; values: number[][] }
    entries: CrossObjectEntry[]
}

export function CrossObjectTab() {
    const { t } = useTranslation()

    const { data, isLoading } = useQuery({
        queryKey: ['analytics', 'cross-object'],
        queryFn: async () => {
            const res = await api.get('/api/analytics/cross-object')
            return (res.data?.data || res.data || {}) as CrossObjectData
        },
    })

    const heatmapOption = useMemo((): EChartsOption => {
        const hm = data?.heatmap
        if (!hm || !hm.dates?.length || !hm.objects?.length) return {}

        // Flatten values into [xIdx, yIdx, value] triples
        const flatData: number[][] = []
        hm.values.forEach((row, yIdx) => {
            row.forEach((val, xIdx) => {
                flatData.push([xIdx, yIdx, val ?? 0])
            })
        })

        return {
            tooltip: {
                position: 'top',
                formatter: (params: any) => {
                    const [xIdx, yIdx, val] = params.data
                    return `${hm.dates[xIdx]}<br/>${hm.objects[yIdx]}<br/>Score: <b>${val}</b>`
                },
            },
            grid: { left: 120, right: 20, top: 10, bottom: 60 },
            xAxis: {
                type: 'category',
                data: hm.dates,
                axisLabel: { fontSize: 10, color: '#888', rotate: 45 },
                axisTick: { show: false },
                axisLine: { show: false },
            },
            yAxis: {
                type: 'category',
                data: hm.objects,
                axisLabel: { fontSize: 10, color: '#888' },
                axisTick: { show: false },
                axisLine: { show: false },
            },
            visualMap: {
                min: 0,
                max: 8,
                calculable: true,
                orient: 'horizontal',
                left: 'center',
                bottom: 0,
                inRange: { color: ['#22c55e', '#f59e0b', '#f97316', '#ef4444'] },
            },
            series: [
                {
                    type: 'heatmap',
                    data: flatData,
                    emphasis: { itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.3)' } },
                },
            ],
        }
    }, [data])

    const entries = data?.entries || []

    return (
        <Card>
            <CardHeader>
                <CardTitle>{t('analytics.crossObject', 'Cross-Object Patterns')}</CardTitle>
            </CardHeader>
            <CardContent>
                {isLoading ? (
                    <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                        <Loader2 className="h-5 w-5 animate-spin mr-2" />
                        Loading cross-object data...
                    </div>
                ) : !data?.heatmap?.dates?.length ? (
                    <div className="flex items-center justify-center h-[200px] text-sm text-muted-foreground">
                        No cross-object pattern data available.
                    </div>
                ) : (
                    <>
                        <ReactECharts option={heatmapOption} style={{ height: 350 }} opts={{ renderer: 'canvas' }} />

                        {entries.length > 0 && (
                            <div className="mt-6 overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b text-left text-muted-foreground">
                                            <th className="pb-2 pr-4 font-medium">Date</th>
                                            <th className="pb-2 pr-4 font-medium">Object</th>
                                            <th className="pb-2 pr-4 font-medium">Type</th>
                                            <th className="pb-2 pr-4 font-medium">Description</th>
                                            <th className="pb-2 pr-4 font-medium">Score</th>
                                            <th className="pb-2 font-medium">Vault</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {entries.map((e, i) => (
                                            <tr key={i} className="border-b last:border-0">
                                                <td className="py-2 pr-4 whitespace-nowrap">{new Date(e.date).toLocaleDateString()}</td>
                                                <td className="py-2 pr-4 font-mono text-xs">{e.objectID}</td>
                                                <td className="py-2 pr-4">{e.type || '—'}</td>
                                                <td className="py-2 pr-4 max-w-[300px] truncate">{e.description || '—'}</td>
                                                <td className="py-2 pr-4 font-semibold">{e.score}</td>
                                                <td className="py-2">{e.vaultName || '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    )
}
